import React, { useState, useRef, useEffect } from 'react'
import { PlusIcon, PaperAirplaneIcon, MicrophoneIcon, StopIcon } from '@heroicons/react/24/solid'
import PulseLoader from 'react-spinners/PulseLoader'
import EtContentEditorLight2 from '@/utils/EtContentEditorLight2'
import { useCreateNewUserImpactMutation } from '@/features/userImpacts/userImpactsApiSlice'
import { useGetContactsQuery } from '@/features/contacts/contactsApiSlice'
import { useGetImpactTypesQuery } from '@/features/impactTypes/impactTypesApiSlice'

const EtCreateUserImpactLandingComp = ({
  userId,
  userOrgId,
  onClose = () => {},
  refetchUserImpacts = () => {},
}) => {

  // First Created: 08 Apr 2025  
  // Author: Atul Pandey  
  // Purpose: Create a standalone component to add a new user impact from Trinity Web Landing 
  // Updated - 23 Apr 2025 - added flag for marking an impact as a magic moment - markAsMagicMoment flag 
  
  // 1️⃣ ⬇ State Variables
  const [impactText, setImpactText] = useState('')
  const [selectedContactId, setSelectedContactId] = useState('')
  const [selectedImpactTypeId, setSelectedImpactTypeId] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [aiGeneratedResponse, setAiGeneratedResponse] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const [markAsMagicMoment, setMarkAsMagicMoment] = useState(false); // 23 Apr 2025

  const textareaRef = useRef(null)
  const recognitionRef = useRef(null)

  // 2️⃣ ⬇ Queries & Mutation
  const { data: contactsData } = useGetContactsQuery()
  const { data: impactTypesData } = useGetImpactTypesQuery()
  const [createNewUserImpact] = useCreateNewUserImpactMutation()

  const extractEntities = (adapter) => {
    if (!adapter?.ids || !adapter?.entities) return []
    return adapter.ids.map(id => adapter.entities[id])
  }

  const contacts = extractEntities(contactsData)
  const impactTypes = extractEntities(impactTypesData)

  // 3️⃣ ✅ Handle Submit
  const handleSubmit = async (finalText) => {
    // Validation
    if (!selectedImpactTypeId) {
      alert("Please select a Impact Type");
      return;
    }

    if (!finalText.trim()) {
        alert("Impact note cannot be empty.");
        return;
      }
  
    // if (!impactText || impactText.trim() === "") {
    //     alert("Impact note cannot be empty.");
    //     return;
    //   }
  
    // Build the payload
    const payload = {
      userId,
      userOrgId
    };
  
    if (selectedContactId) {
      // Contact-specific note
      payload.userImpactContactNotes = [
        {
          userImpactTypeId: selectedImpactTypeId,
          userContactId: selectedContactId,
          userImpactContactNoteText: finalText,
          userImpactContactNoteDate: new Date().toISOString(),
          userImpactContactNoteUpdateDate: new Date().toISOString(),
          markAsMagicMoment // 23 Apr 2025
        }
      ];
    } else {
      // Self note
      payload.userImpactAllNotes = [
        {
          userImpactTypeId: selectedImpactTypeId,
          userImpactAllNoteText: finalText,
          userImpactAllNoteDate: new Date().toISOString(),
          userImpactAllNoteUpdateDate: new Date().toISOString(),
          markAsMagicMoment // 23 Apr 2025
        }
      ];
    }
  
    try {
      await createNewUserImpact(payload).unwrap();
  
      // Reset the form
      setSelectedContactId("");
      setSelectedImpactTypeId("");
      setImpactText("");
      setMarkAsMagicMoment(false); // ✅ 23 Apr 2025 Reset the checkbox
  
      // Refetch if provided
      if (refetchUserImpacts) {
        refetchUserImpacts();
      }
  
      if (onClose) {
        onClose();
      }
  
    } catch (err) {
      console.error("Error creating impact:", err);
      alert("Failed to create impact. Please try again.");
    }
  };
  
    // 4️⃣ ⬇ AI Generation Helpers

    // 4a - aiPrompt
    const aiPrompt = `
    Summarize the following text into a concise, well-structured note in **HTML format**. Organize key points into a numbered list, with the first word of each item wrapped in <strong> tags for emphasis. Ensure the output is clean and readable, with paragraphs and spacing between sections. Do not include a subject line or closing statement. Do not include any salutations like "Dear Team" etc. Only return the HTML without additional comments:
  
    "${impactText}"
    `;
  
    // 4b - Function to process response chunks
    const handleResponseChunk = (chunk) => {
      setAiGeneratedResponse((prev) => prev + chunk)
    }
  
    // 4c - Function to process final response once ai stream is complete
    const handleComplete = (finalResponse) => {
      setIsModalOpen(true)
      setAiGeneratedResponse("")
      setTimeout(() => simulateTypingEffect(finalResponse), 500)
      setLoading(false)
    }
  
    // 4d - Handle errors during the AI generation process
    const handleError = (error) => {
      console.error("Error generating AI response:", error)
      alert("Something went wrong. Please try again.")
      setLoading(false)
    }
  
    // 4e - Generate AI reponse function
    const generateAIResponse = (prompt, onResponseChunk, onComplete, onError) => {
      try {
        const eventSource = new EventSource(
          `${process.env.REACT_APP_SERVER_URL}/trinity/generateResponse?` +
          `messages=${encodeURIComponent(JSON.stringify([
            { role: "system", content: "Generate a refined professional note." },
            { role: "user", content: prompt }
          ]))}&maxTokens=5000&model=${encodeURIComponent("gpt-4")}`
        )
  
        let response = ""
  
        eventSource.onmessage = (event) => {
          const chunk = event.data
          response += chunk
          onResponseChunk(chunk)
        }
  
        eventSource.addEventListener("end", () => {
          eventSource.close()
          onComplete(response)
        })
  
        eventSource.onerror = (error) => {
          eventSource.close()
          onError(error)
        }
      } catch (err) {
        onError(err)
      }
    }
  
    // 4f - Function to generate response
    const handleGenerateAIResponse = () => {
      if (!impactText.trim()) return
  
      setIsModalOpen(true)
      setLoading(true)
  
      generateAIResponse(aiPrompt, handleResponseChunk, handleComplete, handleError)
    }
  
    // 5 - Simulate typing effect
    const simulateTypingEffect = (text) => {
      let index = 0
      const words = text.split(" ")
      let displayedText = ""
  
      const interval = setInterval(() => {
        if (index < words.length) {
          displayedText += words[index] + " "
          setAiGeneratedResponse(displayedText)
          index++
        } else {
          clearInterval(interval)
        }
      }, 100)
    }
  

  return (
    <div className="p-4 bg-white rounded shadow-md font-poppins text-sm">
      <p className="text-lg font-bold text-orange-600 mb-2">Add a New Impact</p>

      {/* Contact Dropdown */}
      <div className="mb-2">
        <label className="block text-xs font-bold text-gray-600">Contact (optional)</label>
        <select
          value={selectedContactId}
          onChange={(e) => setSelectedContactId(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded"
        >
          <option value="">Self</option>
          {contacts.map((c) => (
            <option key={c._id} value={c._id}>
              {c.contactName} - {c.contactTypeId?.contactTypeName}
            </option>
          ))}
        </select>
      </div>

      {/* Type Dropdown */}
      <div className="mb-2">
        <label className="block text-xs font-bold text-gray-600">Impact Type</label>
        <select
          value={selectedImpactTypeId}
          onChange={(e) => setSelectedImpactTypeId(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded"
        >
          <option value="">Select Type</option>
          {impactTypes.map((t) => (
            <option key={t._id} value={t._id}>
              {t.impactTypeName}
            </option>
          ))}
        </select>
      </div>

      {/* Editor */}
      <div className="mb-2">
        <label className="block text-xs font-bold text-gray-600">Note</label>
        <EtContentEditorLight2
          value={impactText}
          handleChange={setImpactText}
          placeholderText="Write your impact here..."
          editorKey={`impact-note`}
        />
      </div>

      {/* 23 Apr 2025 - Mark as Magic Moment */}
      <div className="flex items-center space-x-2 mb-2">
        <input
          type="checkbox"
          id="markAsMagicMoment"
          checked={markAsMagicMoment}
          onChange={() => setMarkAsMagicMoment(prev => !prev)}
          className="w-4 h-4"
        />
        <label htmlFor="markAsMagicMoment" className="text-xs text-gray-700 font-medium">
          Mark this as a Magic Moment
        </label>
      </div>


      {/* Button Bar */}
      <div className="flex justify-end space-x-2 mt-3">
        <button
          onClick={() => setIsConfirmModalOpen(true)}
          className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 flex items-center"
        >
          <PaperAirplaneIcon className="h-4 w-4 mr-1" />
          Submit
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
        >
          Cancel
        </button>
      </div>

      {/* Modals: AI Confirm + AI Output → to be added next step */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-lg font-semibold mb-4">Need Trinity's touch?</h2>
            <p className="text-sm text-gray-600 mb-4">
                Would you like to refine your note with Trinity, or submit it directly?
            </p>
            <div className="flex justify-end space-x-4">
                <button
                className="p-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                onClick={() => {
                    setIsConfirmModalOpen(false);
                    handleSubmit(impactText); // Submit directly
                }}
                >
                Submit Directly
                </button>
                <button
                className="p-2 bg-orange-600 text-white rounded hover:bg-orange-700"
                onClick={() => {
                    setIsConfirmModalOpen(false);
                    handleGenerateAIResponse(); // Refine with AI
                }}
                >
                Refine with Trinity
                </button>
            </div>
            </div>
        </div>
      )}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-lg font-semibold mb-4">Review and Edit</h2>

            {loading ? (
                <div className="flex justify-start items-center">
                <p className="text-sm mx-1 px-1">Generating response…</p>
                <div className="bg-orange-600 p-2 rounded">
                    <PulseLoader color={"#fff"} />
                </div>
                </div>
            ) : (
                <>
                {/* Original Note */}
                <div className="mb-4">
                    <h3 className="text-sm font-bold text-gray-600">Original Note</h3>
                    <textarea
                    value={impactText}
                    readOnly
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg p-3 bg-gray-100 focus:outline-none overflow-y-auto"
                    />
                </div>

                {/* Refined Note */}
                <div className="mb-4">
                    <h3 className="text-sm font-bold text-gray-600">Trinity Refined Note</h3>
                    {(() => {
                    const plainTextResponse = aiGeneratedResponse.replace(/<[^>]+>/g, '');
                    return (
                        <textarea
                        value={plainTextResponse}
                        onChange={(e) => setAiGeneratedResponse(e.target.value)}
                        rows={6}
                        className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none resize-y"
                        />
                    );
                    })()}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-4 mt-4">
                    <button
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    onClick={() => setIsModalOpen(false)}
                    >
                    Cancel
                    </button>
                    <button
                    className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
                    onClick={() => {
                        const combinedText = `<strong>Trinity Refined:</strong>\n\n${aiGeneratedResponse}\n\n<strong>My Note:</strong>\n\n${impactText}`;
                        setImpactText(combinedText);
                        setIsModalOpen(false);
                        handleSubmit(combinedText); // Final submission
                    }}
                    disabled={loading}
                    >
                    Confirm
                    </button>
                </div>
                </>
            )}
            </div>
        </div>
       )}

    </div>
  )
}

export default EtCreateUserImpactLandingComp